import jsPDF from 'jspdf'
import 'jspdf-autotable'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import { currentDateTime, wtFormatDate } from 'src/helpers/commons/moment'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtFormatMoney } from 'src/helpers/commons/utils'

const user = wtGetFromLocalStorage('authUser')

const logo = 'assets/wil-icon.png'
const totalPagesExp = '{total_pages_count_string}'
const outletColumnColors = [
  [127, 29, 29],
  [22, 101, 52],
  [30, 64, 175],
  [133, 77, 14],
  [12, 74, 110],
  [157, 23, 77],
  [91, 33, 182],
  [55, 48, 163],
]

const buildStatementRows = (reportData) => {
  const rows = (reportData?.rows || []).map((row) => [
    {
      content: row.description || '',
      styles: { halign: 'left' },
    },
    ...((reportData?.outlets || []).map((outlet) => {
      const outletAmount = (row.outlet_amounts || []).find((item) => +item.outlet_id === +outlet.id)
      return {
        content: wtFormatMoney(outletAmount?.amount || 0, '', true, 0),
        styles: { halign: 'right' },
      }
    })),
    {
      content: wtFormatMoney(row.total || 0, '', true, 0),
      styles: { halign: 'right' },
    },
    { content: false, styles: { display: 'none' } },
  ])

  rows.push([
    {
      content: 'Total',
      styles: { halign: 'left', fontStyle: 'bold' },
    },
    ...((reportData?.outlets || []).map((outlet) => ({
      content: wtFormatMoney(outlet.total || 0, '', true, 0),
      styles: { halign: 'right', fontStyle: 'bold' },
    }))),
    {
      content: wtFormatMoney(reportData?.grand_total || 0, '', true, 0),
      styles: { halign: 'right', fontStyle: 'bold' },
    },
    { content: true, styles: { display: 'none' } },
  ])

  return rows
}

const buildNotesRows = (reportData) => {
  return (reportData?.outlets || []).flatMap((outlet, outletIndex) => {
    const outletSections = (reportData?.rows || []).flatMap((row, rowIndex) => {
      const outletDetails = row.cell_details?.[outlet.id] || []

      if (!outletDetails.length) {
        return []
      }

      return [
        [
          {
            content: `${outletIndex + 1}.${rowIndex + 1}`,
            styles: { halign: 'left', fontStyle: 'bold' },
          },
          {
            content: '',
            styles: { halign: 'left', fontStyle: 'bold' },
          },
          {
            content: row.description || '',
            styles: { halign: 'left', fontStyle: 'bold' },
          },
          {
            content: '',
            styles: { halign: 'right' },
          },
          { content: 'head', styles: { display: 'none' } },
        ],
        ...outletDetails
          .slice()
          .sort((a, b) => `${a.account_code || ''}`.localeCompare(`${b.account_code || ''}`, undefined, { numeric: true }))
          .map((detail) => [
            {
              content: detail.note_id || detail.note || detail.note_no || '',
              styles: { halign: 'left' },
            },
            {
              content: detail.account_code || '',
              styles: { halign: 'left' },
            },
            {
              content: detail.account_name || detail.description || '',
              styles: { halign: 'left' },
            },
            {
              content: wtFormatMoney(detail.amount || 0, '', true, 2),
              styles: { halign: 'right', fontSize: 14, textColor: [0, 51, 153] },
            },
            { content: 'detail', styles: { display: 'none' } },
          ]),
        [
          {
            content: '',
            styles: { halign: 'left' },
          },
          {
            content: '',
            styles: { halign: 'left' },
          },
          {
            content: 'Closing Total',
            styles: { halign: 'left', fontStyle: 'bold' },
          },
          {
            content: wtFormatMoney(
              ((row.outlet_amounts || []).find((item) => +item.outlet_id === +outlet.id)?.amount) || 0,
              '',
              true,
              2,
            ),
            styles: { halign: 'right', fontStyle: 'bold', fontSize: 14, textColor: [0, 51, 153] },
          },
          { content: 'sub_total', styles: { display: 'none' } },
        ],
      ]
    })

    if (!outletSections.length) {
      return []
    }

    return [
      [
        {
          content: `${outletIndex + 1}`,
          styles: { fontStyle: 'bold', halign: 'left' },
        },
        {
          content: '',
          styles: { fontStyle: 'bold', halign: 'left' },
        },
        {
          content: outlet.name || '',
          styles: { fontStyle: 'bold', halign: 'left' },
        },
          {
            content: wtFormatMoney(outlet.total || 0, '', true, 2),
            styles: { halign: 'right', fontStyle: 'bold', fontSize: 14, textColor: [0, 51, 153] },
          },
        { content: 'outlet', styles: { display: 'none' } },
      ],
      ...outletSections,
      [
        {
          content: '',
          styles: { halign: 'left' },
        },
        {
          content: '',
          styles: { halign: 'left' },
        },
        {
          content: 'Outlet Total',
          styles: { halign: 'left', fontStyle: 'bold' },
        },
          {
            content: wtFormatMoney(outlet.total || 0, '', true, 2),
            styles: { halign: 'right', fontStyle: 'bold', fontSize: 14, textColor: [0, 51, 153] },
          },
        { content: 'outlet_total', styles: { display: 'none' } },
      ],
    ]
  })
}

const getOrientation = (outletCount) => (outletCount > 3 ? 'landscape' : 'portrait')

const getPageWidth = (doc) => {
  const pageSize = doc.internal.pageSize
  return pageSize.width ? pageSize.width : pageSize.getWidth()
}

const getPageHeight = (doc) => {
  const pageSize = doc.internal.pageSize
  return pageSize.height ? pageSize.height : pageSize.getHeight()
}

const drawHeader = (doc, title, subtitle) => {
  const pageWidth = getPageWidth(doc)

  doc.setFontSize(11)
  doc.setTextColor('#3c4b64')
  doc.setFont('Helvetica', 'bold')
  doc.addImage(logo, 'PNG', 5, 4, 25, 25)
  doc.line(32, 6, 32, 25)

  doc.text('WAH', 35, 10, { align: 'left' })
  doc.text('INDUSTRIES', 35, 15)
  doc.text('LIMITED', 35, 20)
  doc.setFontSize(9)
  doc.text('www.wahindustries.com', 35, 25)
  doc.setFontSize(11)
  doc.text(title, pageWidth - 100, 15, { align: 'left' })
  doc.text(subtitle, pageWidth - 100, 22, { align: 'left' })
  doc.line(10, 30, pageWidth - 10, 30)
}

const drawFooter = (doc) => {
  const pageNo = doc.internal.getNumberOfPages()
  const pageHeight = getPageHeight(doc)
  let str = `Page ${pageNo}`

  if (typeof doc.putTotalPages === 'function') {
    str = `${str} of ${totalPagesExp}`
  }

  doc.setFontSize(10)
  doc.setTextColor('black')
  doc.setFont('Helvetica', 'normal')
  doc.text('This report is printed through ERP at ', 60, pageHeight - 15)

  doc.setTextColor('#0202f7')
  doc.setFont('Helvetica', 'bold')
  doc.text(currentDateTime(), 124, pageHeight - 15)

  doc.setTextColor('black')
  doc.setFontSize(9)
  doc.line(14, pageHeight - 11, getPageWidth(doc) - 14, pageHeight - 11)
  doc.text(str, getPageWidth(doc) - 14, pageHeight - 7, { align: 'right' })
  doc.text('Printed By ', 20, pageHeight - 7, { align: 'left' })
  doc.setTextColor('blue')
  doc.text(`${user ? user.name : ''}`, 37, pageHeight - 7, { align: 'left' })
}

const OutletExpenseReportPdf = (reportData, fromDate, toDate) => {
  if (!(reportData?.rows || []).length || !(reportData?.outlets || []).length) {
    lzNotifyErrorMessageFromResult('No Data found!')
    return
  }

  const orientation = getOrientation(reportData.outlets.length)
  const doc = new jsPDF({ orientation })
  const outletCount = reportData.outlets.length
  const pageWidth = getPageWidth(doc)
  const descriptionWidth = Math.max(48, outletCount > 4 ? 42 : 60)
  const totalWidth = 32
  const availableOutletWidth = pageWidth - 20 - descriptionWidth - totalWidth
  const outletWidth = Math.max(22, availableOutletWidth / Math.max(outletCount, 1))

  doc.autoTable({
    head: [[
      { content: 'Description', styles: { halign: 'left' } },
      ...reportData.outlets.map((outlet) => ({
        content: outlet.name,
        styles: { halign: 'center' },
      })),
      { content: 'Total', styles: { halign: 'center' } },
    ]],
    body: buildStatementRows(reportData),
    theme: 'plain',
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [60, 75, 100],
      fontSize: 10,
      fontStyle: 'bold',
    },
    bodyStyles: {
      fontSize: 9,
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: descriptionWidth, halign: 'left' },
      ...reportData.outlets.reduce((acc, _, index) => {
        acc[index + 1] = { cellWidth: outletWidth, halign: 'right' }
        return acc
      }, {}),
      [outletCount + 1]: { cellWidth: totalWidth, halign: 'right' },
    },
    margin: { top: 40, bottom: 20, left: 10, right: 10 },
    didDrawPage: () => {
      drawHeader(
        doc,
        'OUTLET EXPENSE REPORT',
        `${wtFormatDate(fromDate, 'MMMM D, Y')} TO ${wtFormatDate(toDate, 'MMMM D, Y')}`,
      )
      drawFooter(doc)
    },
    willDrawCell: (data) => {
      if (data.row.section === 'head' && data.column.index > 0) {
        const { cell } = data
        data.doc.setDrawColor(0, 0, 0)
        data.doc.setLineWidth(0.6)
        data.doc.line(cell.x + 1, cell.y + cell.height, cell.x + cell.width - 1, cell.y + cell.height)
      }

      if (data.row.section === 'body' && data.row.raw[outletCount + 2]?.content === true) {
        const { cell } = data
        data.doc.setDrawColor(0, 0, 0)
        data.doc.setLineWidth(0.5)
        data.doc.line(cell.x + 1, cell.y, cell.x + cell.width - 1, cell.y)
        data.doc.line(cell.x + 1, cell.y + cell.height, cell.x + cell.width - 1, cell.y + cell.height)
      }
    },
    didParseCell: (data) => {
      if (data.section === 'body' && data.row.raw[outletCount + 2]?.content === true) {
        data.cell.styles.fontStyle = 'bold'
        data.cell.styles.fontSize = 10
      }

      if (data.column.index > 0 && data.column.index <= outletCount) {
        const outletColor = outletColumnColors[(data.column.index - 1) % outletColumnColors.length]

        if (data.section === 'head') {
          data.cell.styles.textColor = outletColor
          data.cell.styles.fontStyle = 'bold'
        }

        if (data.section === 'body') {
          data.cell.styles.textColor = outletColor
        }
      }
    },
  })

  const notesRows = buildNotesRows(reportData)

  if (notesRows.length) {
    doc.addPage(orientation)
    const notesLeftRightMargin = 14
    const noteIdWidth = 24
    const notesAccountCodeWidth = 34
    const notesAmountWidth = 68
    const notesDescriptionWidth =
      pageWidth - (notesLeftRightMargin * 2) - noteIdWidth - notesAccountCodeWidth - notesAmountWidth

    doc.autoTable({
      head: [[
        { content: 'Note ID', styles: { halign: 'left' } },
        { content: 'Account Code', styles: { halign: 'left' } },
        { content: 'Description', styles: { halign: 'left' } },
        { content: 'Amount', styles: { halign: 'right' } },
      ]],
      body: notesRows,
      theme: 'plain',
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [60, 75, 100],
        fontSize: 10,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { cellWidth: noteIdWidth, halign: 'left' },
        1: { cellWidth: notesAccountCodeWidth, halign: 'left' },
        2: { cellWidth: notesDescriptionWidth, halign: 'left' },
        3: { cellWidth: notesAmountWidth, halign: 'right' },
      },
      margin: { top: 40, bottom: 20, left: notesLeftRightMargin, right: notesLeftRightMargin },
      didDrawPage: () => {
        drawHeader(
          doc,
          'NOTES TO OUTLET EXPENSE REPORT',
          `${wtFormatDate(fromDate, 'MMMM D, Y')} TO ${wtFormatDate(toDate, 'MMMM D, Y')}`,
        )
        drawFooter(doc)
      },
      willDrawCell: (data) => {
        if (data.row.section === 'head') {
          const { cell } = data
          data.doc.setDrawColor(0, 0, 0)
          data.doc.setLineWidth(0.6)
          data.doc.line(cell.x + 1, cell.y + cell.height, cell.x + cell.width - 1, cell.y + cell.height)
        }

        const rowType = data.row.raw[4]?.content
        if (data.row.section === 'body' && rowType === 'outlet_total') {
          const { cell } = data
          data.doc.setDrawColor(0, 0, 0)
          data.doc.setLineWidth(0.5)
          data.doc.line(cell.x + 1, cell.y + cell.height, cell.x + cell.width - 1, cell.y + cell.height)
        }
      },
      didParseCell: (data) => {
        const rowType = data.row.raw[4]?.content

        if (data.section === 'body' && (rowType === 'head' || rowType === 'outlet' || rowType === 'sub_total' || rowType === 'outlet_total')) {
          data.cell.styles.fontStyle = 'bold'
        }

        if (data.section === 'body' && rowType === 'outlet') {
          data.cell.styles.fontSize = 10
        }

        if (data.column.index === 3 && data.section === 'body') {
          data.cell.styles.textColor = [0, 51, 153]
          data.cell.styles.fontSize = 14
        }
      },
    })
  }

  if (typeof doc.putTotalPages === 'function') {
    doc.putTotalPages(totalPagesExp)
  }

  const pdfBlob = doc.output('blob')
  const pdfUrl = URL.createObjectURL(pdfBlob)
  window.open(pdfUrl, '_blank')
}

export default OutletExpenseReportPdf
